import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import BorderedBtn from "./FacilityBorderedBtn";

import { Smoking } from "../../common/classes/SonicFacility";

import { regions } from "../../utils/GeneralUtils";

import styles from "./SearchModal.module.scss";

type SearchProps = {
  searchOpen: boolean;
  state?: DisplayState;
  handleSearchModal: (isOpen: boolean) => void;
  keyword: string;
  handleKeyword: (value: string) => void;
  handleSearchFunc: (args: SearchState) => void;
  clearSearch: () => void;
  searchArgs: SearchState;
};

export type SearchState = {
  prefecture: Array<string>;
  hostEvents: string;
  foodService: string;
  smoking: string;
};

export enum DisplayState {
  None = "None",
  Prefcture = "Prefecture",
  Event = "Event",
  Food = "Food",
  Smoking = "Smoking",
}

const SearchModal: React.FC<SearchProps> = ({
  searchOpen,
  state = DisplayState.None,
  handleSearchModal,
  keyword,
  handleKeyword,
  handleSearchFunc,
  clearSearch,
  searchArgs,
}) => {
  const [displayState, setDisplayState] = useState<DisplayState>(state);
  const [prefectureState, setPrefectureState] = useState<Array<string>>(
    searchArgs.prefecture
  );
  const [inputKeyword, setInputKeyword] = useState<string>(keyword);
  const [searchState, setSearchState] = useState<SearchState>({
    prefecture: searchArgs.prefecture,
    hostEvents: searchArgs.hostEvents,
    foodService: searchArgs.foodService,
    smoking: searchArgs.smoking,
  });

  const checkActivePrefecture = (val: string) => {
    return prefectureState.findIndex((pref) => pref === val) !== -1;
  };

  const handlePrefecture = (val: string) => {
    const index = prefectureState.findIndex((pref) => pref === val);
    const newState = prefectureState;
    if (index === -1) {
      newState.push(val);
    } else {
      newState.splice(index, 1);
    }
    setPrefectureState([...newState]);
  };

  const isSmokingType = (val: unknown): val is Smoking => {
    return (
      val === Smoking.Allow ||
      val === Smoking.Separate ||
      val === Smoking.NotAllow
    );
  };

  const handleSearchState = (
    target: keyof SearchState,
    val: string | Smoking | Array<string>
  ) => {
    const currentState = searchState;

    if (target === "smoking" && isSmokingType(val)) {
      const value = currentState[target] === val ? "" : val;
      currentState.smoking = value;
    } else if (target === "prefecture" && Array.isArray(val)) {
      currentState.prefecture = val.length ? val : [];
    } else {
      if (target === "hostEvents" || target === "foodService") {
        const value = currentState[target] === val ? "" : (val as string);
        currentState[target] = value;
      }
    }
    setSearchState({ ...currentState });
    setDisplayState(DisplayState.None);
  };

  useEffect(() => {
    const html = document.querySelector("html");
    if (html) {
      html.style.overflowY = "hidden";

      return () => {
        html.style.overflowY = "unset";
      };
    }
  }, []);

  const genHead = () => (
    <div className={styles["f-facilitySearch__head"]}>
      <div>
        <p>検索</p>
        <span></span>
        <p>Search</p>
      </div>
    </div>
  );

  const genSelect = () => {
    switch (displayState) {
      case DisplayState.Prefcture:
        return (
          <div className={styles["f-facilitySearch__list-select__main-area"]}>
            <div className={styles["f-facilitySearch__list-select__head"]}>
              <FontAwesomeIcon icon={["fas", "map-marker-alt"]} />
              <span>都道府県</span>
            </div>
            <div
              className={`${styles["f-facilitySearch__list-select__body"]} ${styles["prefecture"]}`}
            >
              {regions.map((region) => (
                <div key={region.name}>
                  <h2>{region.name}</h2>
                  <ul>
                    {region.prefectures.map((prefecture) => (
                      <li key={prefecture}>
                        <button
                          className={
                            checkActivePrefecture(prefecture)
                              ? styles["active"]
                              : undefined
                          }
                          onClick={() => handlePrefecture(prefecture)}
                        >
                          {prefecture}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <BorderedBtn
              onClick={() => handleSearchState("prefecture", prefectureState)}
              text="検索条件を追加"
              cls="add"
            />
          </div>
        );
      case DisplayState.Event:
        return (
          <div className={styles["f-facilitySearch__list-select__main-area"]}>
            <div className={styles["f-facilitySearch__list-select__head"]}>
              <FontAwesomeIcon icon={["fas", "trophy"]} />
              <span>イベント開催</span>
            </div>
            <div className={styles["f-facilitySearch__list-select__body"]}>
              <button
                onClick={() => handleSearchState("hostEvents", "event_false")}
                className={
                  searchState.hostEvents === "event_false"
                    ? styles["active"]
                    : undefined
                }
              >
                NG
              </button>
              <button
                onClick={() => handleSearchState("hostEvents", "event_true")}
                className={
                  searchState.hostEvents === "event_true"
                    ? styles["active"]
                    : undefined
                }
              >
                OK
              </button>
            </div>
          </div>
        );
      case DisplayState.Food:
        return (
          <div className={styles["f-facilitySearch__list-select__main-area"]}>
            <div className={styles["f-facilitySearch__list-select__head"]}>
              <FontAwesomeIcon icon={["fas", "utensils"]} />
              <span>フード提供</span>
            </div>
            <div className={styles["f-facilitySearch__list-select__body"]}>
              <button
                onClick={() => handleSearchState("foodService", "food_false")}
                className={
                  searchState.foodService === "food_false"
                    ? styles["active"]
                    : undefined
                }
              >
                なし
              </button>
              <button
                onClick={() => handleSearchState("foodService", "food_true")}
                className={
                  searchState.foodService === "food_true"
                    ? styles["active"]
                    : undefined
                }
              >
                あり
              </button>
            </div>
          </div>
        );
      case DisplayState.Smoking:
        return (
          <div className={styles["f-facilitySearch__list-select__main-area"]}>
            <div className={styles["f-facilitySearch__list-select__head"]}>
              <FontAwesomeIcon icon={["fas", "smoking"]} />
              <span>タバコ</span>
            </div>
            <div
              className={`${styles["f-facilitySearch__list-select__body"]} ${styles["smoke"]}`}
            >
              <button
                onClick={() => handleSearchState("smoking", Smoking.NotAllow)}
                className={
                  searchState.smoking === Smoking.NotAllow
                    ? styles["active"]
                    : undefined
                }
              >
                禁煙
              </button>
              <button
                onClick={() => handleSearchState("smoking", Smoking.Separate)}
                className={
                  searchState.smoking === Smoking.Separate
                    ? styles["active"]
                    : undefined
                }
              >
                分煙
              </button>
              <button
                onClick={() => handleSearchState("smoking", Smoking.Allow)}
                className={
                  searchState.smoking === Smoking.Allow
                    ? styles["active"]
                    : undefined
                }
              >
                喫煙可
              </button>
            </div>
          </div>
        );
    }
  };

  type SearchItemProps = {
    icon: IconProp;
    text: string;
    value: string;
    onClick: () => void;
  };

  const SearchItem: React.FC<SearchItemProps> = ({
    icon,
    text,
    value,
    onClick,
  }) => {
    return (
      <li>
        <button
          className={styles["f-facilitySearch__list-item"]}
          onClick={onClick}
        >
          <div>
            <FontAwesomeIcon icon={icon} className={styles["icon"]} />
            <span>{text}</span>
          </div>
          <div className={`${text === "都道府県" ? styles["prefecture"] : ""}`}>
            <span>{value}</span>
            <FontAwesomeIcon icon={["fas", "angle-right"]} />
          </div>
        </button>
      </li>
    );
  };

  return (
    <div
      className={`${styles["f-facilitySearch"]} ${
        searchOpen ? styles["show"] : ""
      }`}
      id="searchModal"
    >
      <div className={styles["inner"]}>
        {genHead()}
        {displayState === DisplayState.None ? (
          <button
            className={styles["f-facilitySearch__close"]}
            onClick={() => handleSearchModal(false)}
          >
            <FontAwesomeIcon icon={["fas", "times"]} />
          </button>
        ) : null}
        <>
          {displayState !== DisplayState.None ? (
            <div className={styles["f-facilitySearch__list-select"]}>
              {genSelect()}
              <div className={styles["f-facilitySearch__reset"]}>
                <button onClick={() => setDisplayState(DisplayState.None)}>
                  戻る
                </button>
              </div>
            </div>
          ) : (
            <>
              <ul className={styles["f-facilitySearch__list"]}>
                <SearchItem
                  icon={["fas", "map-marker-alt"]}
                  text="都道府県"
                  value={
                    prefectureState.length
                      ? prefectureState.join(" / ")
                      : "未選択"
                  }
                  onClick={() => setDisplayState(DisplayState.Prefcture)}
                />
                <SearchItem
                  icon={["fas", "trophy"]}
                  text="イベント開催"
                  value={
                    searchState.hostEvents === "event_true"
                      ? "OK"
                      : searchState.hostEvents === "event_false"
                      ? "NG"
                      : "未選択"
                  }
                  onClick={() => setDisplayState(DisplayState.Event)}
                />
                <SearchItem
                  icon={["fas", "utensils"]}
                  text="フード提供"
                  value={
                    searchState.foodService === "food_true"
                      ? "あり"
                      : searchState.foodService === "food_false"
                      ? "なし"
                      : "未選択"
                  }
                  onClick={() => setDisplayState(DisplayState.Food)}
                />
                <SearchItem
                  icon={["fas", "smoking"]}
                  text="タバコ"
                  value={
                    searchState.smoking === Smoking.Allow
                      ? "喫煙可"
                      : searchState.smoking === Smoking.Separate
                      ? "分煙"
                      : searchState.smoking === Smoking.NotAllow
                      ? "禁煙"
                      : "未選択"
                  }
                  onClick={() => setDisplayState(DisplayState.Smoking)}
                />
              </ul>
              <div className={styles["f-facilitySearch__word"]}>
                <div>
                  <FontAwesomeIcon icon={["far", "keyboard"]} />
                  <span>フリーワード</span>
                </div>
                <input
                  placeholder="ワードを入力してください"
                  onChange={(e) => {
                    handleKeyword(e.target.value);
                    setInputKeyword(e.target.value);
                  }}
                  value={inputKeyword}
                />
              </div>
              <BorderedBtn
                onClick={() => {
                  handleSearchFunc(searchState);
                  handleSearchModal(false);
                }}
                icon={["fas", "magnifying-glass"]}
                text="検索"
              />
              <div className={styles["f-facilitySearch__reset"]}>
                <button
                  onClick={() => {
                    setPrefectureState([]);
                    setSearchState({
                      prefecture: [],
                      hostEvents: "",
                      foodService: "",
                      smoking: "",
                    });
                    clearSearch();
                    handleSearchModal(false);
                  }}
                >
                  条件リセット
                </button>
              </div>
            </>
          )}
        </>
      </div>
    </div>
  );
};

export default SearchModal;
